import { LinkTypes } from '@modules/card/enums/link-types.enum';
import { TargetType } from '@modules/card/enums/target-type.enum';
import { CardLinkInterface } from '@modules/card/interfaces/card-link.interface';
import { AssetType } from '@modules/cards/types/asset.types';
import { FavorableContent } from '@modules/favorites/interfaces/favorable-content.interface';
import { FavoriteDataInterface } from '@modules/favorites/interfaces/favorite-data.interface';
import { MediaImageModel } from '@modules/media/models/media-image.model';
import { MediaVideoModel } from '@modules/media/models/media-video.model';
import { FileSizePipe } from '@modules/pipes/file-size.pipe';
import { Action } from '@modules/shared/action-button-group/action.types';
import { TrackingItem } from '@modules/shared/gtm/data-layer.types';
import { DownloadableContent, DownloadData } from '@modules/shared/services/download.service';
import { SharableContent, SharingData } from '@modules/shared/services/share.service';
import { MetaItemInterface } from '@modules/ui/meta-items/meta-items.component';
import { filetypeFromMime } from 'src/helpers/strings';

// API types
type DataType =
  | 'article'
  | 'presskit'
  | 'image'
  | 'video'
  | 'document'
  | 'audio'
  | 'media_collection'
  | 'page'
  | 'event_page'
  | 'topic_page'
  | 'media'
  | 'technical_data';

const PRESS_RELEASE_TYPE_MAP: { [k: string]: AssetType } = {
  mb_article: 'PRESS_INFORMATION',
  mb_presskit: 'PRESS_KIT',
};

const ASSET_TO_FAV_DATA_TYPE_MAP: { [k in AssetType]?: DataType } = {
  PRESS_INFORMATION: 'article',
  PRESS_KIT: 'presskit',
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  TECHNICAL_DATA: 'technical_data',
  MEDIA_COLLECTION: 'media_collection',
  AUDIO: 'audio',
};

const ASSET_TO_DOWNLOAD_DATA_TYPE_MAP: { [k in AssetType]?: DataType } = {
  PRESS_INFORMATION: 'article',
  PRESS_KIT: 'presskit',
  IMAGE: 'media',
  VIDEO: 'media',
  DOCUMENT: 'media',
  TECHNICAL_DATA: 'media',
  MEDIA_COLLECTION: 'media_collection',
  AUDIO: 'media',
};

const ASSET_TO_TRACKING_DATA_TYPE_MAP: {
  [k in AssetType]?:
    | 'image'
    | 'video'
    | 'document'
    | 'audio'
    | 'capture'
    | 'press_information'
    | 'press_kit'
    | 'press_kit_article'
    | 'media_collection'
    | 'technical_data';
} = {
  PRESS_INFORMATION: 'press_information',
  PRESS_KIT: 'press_kit',
  IMAGE: 'image',
  VIDEO: 'video',
  DOCUMENT: 'document',
  MEDIA_COLLECTION: 'media_collection',
  AUDIO: 'audio',
  TECHNICAL_DATA: 'technical_data',
};

/**
 * Used for modelling new card data from search results
 */
export class SearchResult implements DownloadableContent, FavorableContent, SharableContent {
  public id: string;
  public marsOriginId?: string;
  public marsShelfNumber?: string;

  public type: AssetType;
  public topline: string;
  public toplineIcon: string;
  public displayDate?: Date;
  public headline: string;
  public text?: string;
  public fuelLabel?: string;
  public media?: MediaImageModel | MediaVideoModel;
  public link: CardLinkInterface;
  public metaItems: MetaItemInterface[];
  public actions: Action[] = [{ type: 'SHARE' }, { type: 'DOWNLOAD' }, { type: 'BOOKMARK' }];

  // for videos (and audios?) TODO generalize this field
  public videoDuration?: number;
  public assetCount: number;

  // for press contact
  public fullname?: string;
  public initials?: string;
  public position?: string;
  public email?: string;
  public phone?: string;
  public x?: string;
  public linkedIn?: string;

  // for event
  public titleHash?: string;
  public startDate?: Date;
  public endDate?: Date;
  public timezoneOffset?: string;
  public location?: string;

  // Data for download, favs and sharing
  public downloadData?: DownloadData;
  public favoriteData: FavoriteDataInterface;
  public shareData: SharingData;
  public trackingData: TrackingItem;

  /**
   * Parses data such that it can be directly used for rendering the card
   *
   * @param data
   */
  constructor(data: any, documentLocation: Location) {
    //  TODO: typed input data
    this.id = data.uuid || data.id;
    this.marsOriginId = data.mars_origin_id;
    this.marsShelfNumber = data.mars_shelf_number;

    // Asset type
    this.type =
      data.type === 'PRESS_RELEASE'
        ? (PRESS_RELEASE_TYPE_MAP[data.press_release_type] ?? 'PRESS_INFORMATION')
        : data.type;

    // Topline
    this.topline = [data.location, data.display_date].filter(Boolean).join(', ');
    this.toplineIcon = data.location ? 'bds/location/24' : 'bds/calendar/24';
    this.displayDate = data.raw_display_date ? new Date(data.raw_display_date) : undefined;
    // Headline
    this.headline = data.title || data.name;

    // Text
    this.text = data.text; // TODO: only for media-specials?

    // Fuel label (legal requirement)
    this.fuelLabel = data.fuel_label;

    // Media: Image or Video
    this.media = data.media;

    // Link, defines the click action
    switch (this.type) {
      case 'PRESS_INFORMATION':
        this.link = {
          type: LinkTypes.ROUTER,
          url: `/article/${data.uuid || data.id}`,
          query: '',
          target: TargetType.SELF,
        };
        break;
      case 'PRESS_KIT':
        this.link = {
          type: LinkTypes.ROUTER,
          url: `/press-kit/${data.uuid || data.id}`,
          query: '',
          target: TargetType.SELF,
        };
        break;
      case 'MEDIA_SPECIAL':
        // TODO: not clear where and how to route
        this.link = {
          type: LinkTypes.ROUTER,
          url: `/${data.slug}`.replace('/page/', ''),
          query: '',
          target: TargetType.SELF,
        };
        break;
      case 'IMAGE':
        this.link = {
          type: LinkTypes.ROUTER,
          url: './',
          outlets: { lightbox: ['image', data.uuid || data.id] },
          query: '',
          target: TargetType.SELF,
        };
        break;
      case 'MEDIA_COLLECTION': {
        // TODO: not clear where and how to route
        this.link = {
          type: LinkTypes.ROUTER,
          url: `/${data.uuid || data.id}`,
          query: '',
          target: TargetType.SELF,
        };
        break;
      }
      case 'VIDEO': {
        this.link = {
          type: LinkTypes.ROUTER,
          url: './',
          outlets: { lightbox: ['video', data.uuid || data.id] },
          query: '',
          target: TargetType.SELF,
        };
        break;
      }
      case 'DOCUMENT': {
        this.link = {
          type: LinkTypes.ROUTER,
          url: './',
          outlets: { lightbox: ['document', data.uuid || data.id] },
          query: '',
          target: TargetType.SELF,
        };
        break;
      }
      case 'TECHNICAL_DATA': {
        this.link = {
          type: LinkTypes.ROUTER,
          url: './',
          outlets: { lightbox: ['technical_data', data.uuid || data.id] },
          query: '',
          target: TargetType.SELF,
        };
        break;
      }
      case 'EVENT': {
        // TODO: TBD: decide if we filter out events without a slug before
        const eventPageSlug = data.event_page_slug?.replace('event-page/', '');
        const pageSlug = data.page_slug?.replace('page/', '');
        const slug = eventPageSlug || pageSlug;
        if (slug) {
          this.link = {
            type: LinkTypes.ROUTER,
            url: `/events/${slug}`,
            query: '',
            target: TargetType.SELF,
          };
        } else {
          this.link = {
            type: LinkTypes.NONE,
            url: '#',
            query: '',
            target: TargetType.SELF,
          };
        }
        break;
      }
      default: {
        this.link = {
          type: LinkTypes.NONE,
          url: '#',
          query: '',
          target: TargetType.SELF,
        };
        break;
      }
    }
    // Actions: if needed, we can change the default actions based on some input data
    switch (this.type) {
      case 'MEDIA_SPECIAL': {
        this.actions = [{ type: 'SHARE' }];
        break;
      }
      default: {
        this.actions = [{ type: 'SHARE' }, { type: 'DOWNLOAD' }, { type: 'BOOKMARK' }];
      }
    }

    // Only for videos: duration (TODO: make this generic "top right label" ?)
    this.videoDuration = data.playing_time;
    this.assetCount = data.page_count;

    // Only for contacts
    this.fullname = `${data.first_name} ${data.last_name}`;
    this.initials = `${data.first_name?.charAt(0)}${data.last_name?.charAt(0)}`;
    this.position = data.position;
    this.email = data.email;
    this.phone = data.phone;
    this.linkedIn = data.linkedin;
    this.x = data.twitter;

    // Only for events
    this.titleHash = data.hashtag;
    this.startDate = data.start_date ? new Date(data.start_date) : undefined;
    this.endDate = data.end_date ? new Date(data.end_date) : undefined;
    this.timezoneOffset = data.timezoneOffset;
    this.location = `${data.city}, ${data.country}`;

    // Meta items (e.g. image_count), hidden when zero
    this.metaItems = [];
    if (data.image_count) {
      this.metaItems.push({
        label: data.image_count === 1 ? 'IMAGE_N' : 'IMAGES_N',
        count: data.image_count,
      });
    }
    if (data.video_count) {
      this.metaItems.push({
        label: data.video_count === 1 ? 'VIDEO_N' : 'VIDEOS_N',
        count: data.video_count,
      });
    }
    if (data.audio_count) {
      this.metaItems.push({
        label: data.audio_count === 1 ? 'AUDIO_N' : 'AUDIOS_N',
        count: data.audio_count,
      });
    }
    if (data.document_count) {
      this.metaItems.push({
        label: data.document_count === 1 ? 'DOCUMENT_N' : 'DOCUMENTS_N',
        count: data.document_count,
      });
    }
    if (data.technical_data_count) {
      this.metaItems.push({
        label: data.technical_data_count === 1 ? 'TECHNICAL_DATA_N' : 'TECHNICAL_DATAS_N',
        count: data.technical_data_count,
      });
    }
    if (data.article_count) {
      this.metaItems.push({
        label: data.article_count === 1 ? 'ARTICLE_N' : 'ARTICLES_N',
        count: data.article_count,
      });
    }

    if (this.type === 'MEDIA_SPECIAL') {
      this.metaItems = [{ label: data.article_count === 1 ? 'CONTENT_N' : 'CONTENTS_N', count: data.article_count }];
    }

    if (['DOCUMENT', 'VIDEO', 'AUDIO', 'IMAGE', 'TECHNICAL_DATA'].includes(this.type)) {
      if (data.mimetype && filetypeFromMime(data.mimetype)) {
        this.metaItems.push({
          label: filetypeFromMime(data.mimetype),
        });
      }
      if (data.filesize) {
        this.metaItems.push({
          label: new FileSizePipe().transform(data.filesize),
        });
      }
    }

    // Create share data
    const origin = documentLocation.origin;
    const pathname = documentLocation.pathname.replace(/\/$/, '');

    if (this.link.outlets) {
      // INFO: if we ever have other outlets, we can extend this part
      const outletUrl = `/(lightbox:${this.link.outlets.lightbox.join('/')})`;
      this.shareData = {
        content: this.text ?? '',
        title: this.headline,
        url: origin + pathname + outletUrl,
      };
    } else {
      this.shareData = {
        content: this.text ?? '',
        title: this.headline,
        url: origin + this.link.url,
      };
    }

    if (!['MEDIA_SPECIAL', 'EVENT', 'PRESS_CONTACT'].includes(this.type)) {
      // Create download data
      this.downloadData = {
        id: data.uuid || data.id,
        type: ASSET_TO_DOWNLOAD_DATA_TYPE_MAP[this.type]!,
        fileType: ASSET_TO_DOWNLOAD_DATA_TYPE_MAP[this.type] ?? '',
        format: data.mimetype ? filetypeFromMime(data.mimetype) : 'ZIP',
        title: this.headline,
        assets: {
          imageCount: data.image_count || (this.type === 'IMAGE' ? 1 : 0),
          videoCount: data.video_count || (this.type === 'VIDEO' ? 1 : 0),
          audioCount: data.audio_count || (this.type === 'AUDIO' ? 1 : 0),
          documentCount: data.document_count || (this.type === 'DOCUMENT' ? 1 : 0),
          technicalDataCount: data.technical_data_count || (this.type === 'TECHNICAL_DATA' ? 1 : 0),
        },
      };
    }

    // Create fav data
    this.favoriteData = {
      id: data.uuid || data.id,
      type: ASSET_TO_FAV_DATA_TYPE_MAP[this.type]!,
    };

    this.trackingData = {
      id: data.uuid || data.id,
      type: ASSET_TO_TRACKING_DATA_TYPE_MAP[this.type]!,
      title: data.title || data.name,
      marsOriginId: this.marsOriginId,
      shelfNumber: this.marsShelfNumber,
    };
  }
}
